import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { Reveal } from '@/components/Reveal';

export default function Solutions() {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Solutions — India & Global | NKP VENTURES"
        description="NKP VENTURES provides digital and business solutions for Indian businesses and reliable outsourcing support for international markets."
        canonical="https://nkpventures.com/solutions"
      />

      {/* Hero */}
      <section className="bg-[#0A1628] pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <p className="overline-label text-teal-400 mb-4">One Company. Two Markets.</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Our Solutions
            </h1>
            <p className="text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
              NKP VENTURES serves two distinct markets — each with its own focus, expertise and service structure. Choose your market to explore what we can do for you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Market Cards */}
      <section className="py-20 bg-[#F7FAFC]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

            {/* India Card */}
            <Reveal delay={0.05}>
              <div className="bg-[#0B3F3B] rounded-2xl p-10 flex flex-col h-full min-h-[480px] relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="relative z-10 flex flex-col h-full">
                  <div className="mb-8">
                    <span className="inline-block bg-white/15 text-white text-xs font-semibold tracking-widest uppercase px-3 py-1.5 rounded-full mb-4">India</span>
                    <h2 className="font-serif text-3xl font-bold text-white mb-3">Digital & Business Solutions</h2>
                    <p className="text-white/70 leading-relaxed">
                      Helping Indian businesses build their brand, strengthen their digital presence and streamline their operations.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 flex-grow mb-8">
                    {[
                      { label: 'Digital Solutions', sub: 'Websites, SEO & Digital Marketing' },
                      { label: 'Creative Solutions', sub: 'Branding, Design & Content' },
                      { label: 'Business Support', sub: 'Admin, Back Office & VA' },
                      { label: 'E-Commerce Support', sub: 'Marketplace & Online Operations' },
                    ].map((item) => (
                      <div key={item.label} className="bg-white/10 rounded-xl p-4">
                        <p className="text-white font-semibold text-sm mb-1">{item.label}</p>
                        <p className="text-white/60 text-xs leading-snug">{item.sub}</p>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/solutions/india"
                    className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#0B3F3B] font-semibold text-sm px-6 py-3 rounded-full transition-colors duration-200 self-start"
                  >
                    Explore India Solutions <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </Reveal>

            {/* Global Card */}
            <Reveal delay={0.12}>
              <div className="bg-[#0F2847] rounded-2xl p-10 flex flex-col h-full min-h-[480px] relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="relative z-10 flex flex-col h-full">
                  <div className="mb-8">
                    <span className="inline-block bg-white/15 text-white text-xs font-semibold tracking-widest uppercase px-3 py-1.5 rounded-full mb-4">Global</span>
                    <h2 className="font-serif text-3xl font-bold text-white mb-3">Outsourcing & Business Solutions</h2>
                    <p className="text-white/70 leading-relaxed">
                      Providing reliable outsourcing and operational support to businesses across international markets.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 flex-grow mb-8">
                    {[
                      { label: 'Business Support', sub: 'Administration & Back Office' },
                      { label: 'Customer Operations', sub: 'Customer & CRM Support' },
                      { label: 'Corporate Functions', sub: 'Finance, Accounting & HR' },
                      { label: 'Specialist Operations', sub: 'Healthcare, E-commerce & Research' },
                    ].map((item) => (
                      <div key={item.label} className="bg-white/10 rounded-xl p-4">
                        <p className="text-white font-semibold text-sm mb-1">{item.label}</p>
                        <p className="text-white/60 text-xs leading-snug">{item.sub}</p>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/solutions/global"
                    className="inline-flex items-center gap-2 bg-[#0E7B73] hover:bg-[#0F9088] text-white font-semibold text-sm px-6 py-3 rounded-full transition-colors duration-200 self-start"
                  >
                    Explore Global Solutions <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Shared CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl font-bold text-[#0A1628] mb-4">
              Not sure which solution fits?
            </h2>
            <p className="text-[#64748B] mb-8">
              Tell us about your business and what you're looking to achieve. We'll recommend the right approach.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#0A1628] hover:bg-[#0F2847] text-white font-semibold px-8 py-4 rounded-full transition-colors duration-200"
            >
              Let's Talk <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
